/**
 * @function ParticipantListTable
 * @description Component for displaying a list of participants.
 * @returns {JSX.Element | null} - The ParticipantListTable component.
 */
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { DateTime } from 'luxon'
import AiBadge from '../../assets/ai.svg'
import { States } from '../../config'
import { formatOutput } from '../../utils/formatOutput'
import { formatParticipantStatus } from '../../utils/formatParticipantStatus'
import { Status } from '../Status'

/**
 * @interface ParticipantListTableProps
 * @description Props for the ParticipantListTable component.
 * @property {Novoic.API.Participant[]} data - The list of participants.
 * @property {(participant: Novoic.API.Participant) => void} onRowClicked - The function to call when a row is clicked.
 */
interface ParticipantListTableProps {
    data?: Novoic.API.Participant[]
    onRowClicked: (participant: Novoic.API.Participant) => void
}

/**
 * @function formatID
 * @description Formats an ID with a prefix.
 * @param {string} prefix - The prefix to format.
 * @param {string} id - The ID to format.
 * @returns {JSX.Element} - The formatted ID.
 */
function formatID(prefix: string, id: string) {
    return formatOutput(prefix + '–' + id.split('-')[0])
}

/**
 * @function formatDate
 * @description Formats a date.
 * @param {string} timestamp - The timestamp to format.
 * @returns {JSX.Element} - The formatted date.
 */
function formatDate(timestamp: string): JSX.Element {
    if (!timestamp) return formatOutput('Today')
    const dt = DateTime.fromISO(timestamp)
    return <span>{dt.toRelative()}</span>
}

/**
 * @function formatParticipant
 * @description Formats a participant.
 * @param {string} patient_id - The patient ID.
 * @param {string} name - The name of the participant.
 * @param {boolean} recommended - Whether the participant is recommended.
 * @param {Novoic.AssignmentStatus} status - The status of the participant.
 * @returns {JSX.Element} - The formatted participant.
 */
function formatParticipant(
    patient_id: string,
    name: string,
    recommended: boolean,
    status: Novoic.AssignmentStatus
): JSX.Element {
    return (
        <div className="flex flex-col gap-y-1">
            <p className="flex text-sm font-medium">
                {formatID('PID', patient_id)}
                {recommended && status === 'complete' && <AiBadge className="ml-2 w-5 h-5" />}
            </p>
            <p className="text-xs font-regular text-gray-600">{formatOutput(name)}</p>
        </div>
    )
}

/**
 * @function renderStatus
 * @description Renders the status of a participant.
 * @param {Novoic.API.Participant} participant - The participant.
 * @returns {JSX.Element} - The status of the participant.
 */
function renderStatus(participant: Novoic.API.Participant) {
    if (!participant.assignment) {
        return formatParticipantStatus('empty', undefined, undefined, undefined)
    }

    return formatParticipantStatus(
        participant.assignment?.status,
        participant.assignment?.score,
        participant.assignment?.level,
        participant.assignment?.updated_at || participant.assignment?.created_at
    )
}

/**
 * @function ParticipantListTable
 * @description Component for displaying a list of participants.
 * @returns {JSX.Element | null} - The ParticipantListTable component.
 */
export function ParticipantListTable({ data, onRowClicked }: ParticipantListTableProps) {
    return (
        <table className="w-full">
            <thead className="nv-thead top-[161px] sticky z-0">
                <tr className="h-12">
                    {['Participant', 'Score', 'Stage', 'Study', 'Site', 'Last active'].map(
                        (col, index) => (
                            <th
                                key={col}
                                scope="col"
                                className={`${
                                    index === 0 ? 'pl-8 pr-2.5' : 'px-2.5'
                                } text-left text-sm font-medium text-gray-900 whitespace-nowrap`}
                            >
                                <button className="group inline-flex">
                                    {col}
                                    <span className="invisible ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible">
                                        <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                </button>
                            </th>
                        )
                    )}
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
                {data?.map((participant: Novoic.API.Participant) => (
                    <tr
                        key={participant.id}
                        className="hover:bg-[#f7f9ff] cursor-pointer transition-all"
                        onClick={() => onRowClicked(participant)}
                    >
                        <td
                            className="pl-8 whitespace-nowrap pr-10 py-4 w-[60px]"
                            role="presentation"
                        >
                            {formatParticipant(
                                participant.studies?.[0]?.external_id || participant.id,
                                participant.name,
                                participant.assignment?.recommend,
                                participant.assignment?.status
                            )}
                        </td>
                        <td className="px-2.5 whitespace-nowrap pr-10 py-4 w-[60px] text-sm">
                            {renderStatus(participant)}
                        </td>
                        <td className="px-2.5 whitespace-nowrap pr-10 py-4 w-[60px] text-sm text-gray-600">
                            <Status
                                status={participant.studies?.[0]?.status as unknown as States}
                            />
                        </td>
                        <td className="px-2.5 whitespace-nowrap pr-10 py-4 text-sm text-gray-600">
                            {formatOutput(participant.studies?.[0]?.study?.name)}
                        </td>
                        <td className="px-2.5 whitespace-nowrap pr-10 py-4 text-sm text-gray-600">
                            {formatOutput(participant.sites?.[0]?.site?.name)}
                        </td>
                        <td className="px-2.5 whitespace-nowrap pr-10 py-4 text-sm text-gray-600">
                            {formatDate(participant.last_activity_at)}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )
}
